import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import Hero from './components/Hero';
import TopicsLexicon from './components/TopicsLexicon';
import BlogSection from './components/BlogSection';
import ProviderShowcase from './components/ProviderShowcase';
import NewsletterSignup from './components/NewsletterSignup';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import { SearchProvider } from './context/SearchProvider';
import { SupabaseProvider } from './context/SupabaseProvider';
import { SupabaseConnectionMonitor } from './components/SupabaseConnectionMonitor';
import SEO from './components/SEO';
import { getSEOConfig } from './config/seoConfig';
import CookieConsent from './components/CookieConsent';
import CookieSettingsButton from './components/CookieSettingsButton';
import { supabase } from './lib/supabase';
import {
  resolveAuthState,
  syncAuthLocalStorageHints,
  isAdminPath,
  isProviderPath,
  isProtectedPath,
  type AuthState,
} from './lib/authGuard';

const DoctorDirectory = lazy(() => import('./components/DoctorDirectory'));
const DoctorDetail = lazy(() => import('./components/DoctorDetail'));
const DoctorRegistration = lazy(() => import('./components/DoctorRegistration'));
const SearchResults = lazy(() => import('./components/SearchResults'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const SymptomsPage = lazy(() => import('./components/SymptomsPage'));
const DiseasesPage = lazy(() => import('./components/DiseasesPage'));
const DiseaseDetail = lazy(() => import('./components/DiseaseDetail'));
const SymptomDetail = lazy(() => import('./components/SymptomDetail'));
const ConsultationsPage = lazy(() => import('./components/ConsultationsPage'));
const ConsultationDetail = lazy(() => import('./components/ConsultationDetail'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const Impressum = lazy(() => import('./components/Impressum'));
const Datenschutz = lazy(() => import('./components/Datenschutz'));
const AGB = lazy(() => import('./components/AGB'));
const ContactForm = lazy(() => import('./components/ContactForm'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const ProviderDashboard = lazy(() => import('./components/ProviderDashboard'));
const ProviderLogin = lazy(() => import('./components/ProviderLogin'));
const ProviderEdit = lazy(() => import('./components/ProviderEdit'));
const ProviderAccountSettings = lazy(() => import('./components/ProviderAccountSettings'));
const EmailChangeVerification = lazy(() => import('./components/EmailChangeVerification'));
const SupabaseDebugger = lazy(() => import('./components/SupabaseDebugger'));
const PremiumBenefits = lazy(() => import('./components/PremiumBenefits'));
const ProviderFachbeitragCreate = lazy(() => import('./components/ProviderFachbeitragCreate'));
const Haftungsausschluss = lazy(() => import('./components/Haftungsausschluss'));
const NewsletterSuccess = lazy(() => import('./components/NewsletterSuccess'));
const NewsletterError = lazy(() => import('./components/NewsletterError'));
const NewsletterVerification = lazy(() => import('./components/NewsletterVerification'));

const HomePage: React.FC = () => {
  const seoData = getSEOConfig('/');

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
      />
      <Hero />
      <ProviderShowcase />
      <TopicsLexicon />
      <BlogSection />
    </>
  );
};

// Not Found component for unmatched routes
const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const cards: Array<{ to: string; title: string; desc: string; emoji: string; color: string }> = [
    { to: '/anbieter', title: 'Anbieter & Praxen', desc: 'Ärztinnen, Therapeuten und Kliniken im Verzeichnis durchsuchen.', emoji: '🩺', color: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50' },
    { to: '/krankheiten', title: 'Krankheiten', desc: 'Hintergründe, Diagnose und Behandlung — verständlich erklärt.', emoji: '📚', color: 'border-red-200 hover:border-red-400 hover:bg-red-50' },
    { to: '/symptome', title: 'Symptome', desc: 'Beschwerden einordnen und mögliche Ursachen verstehen.', emoji: '🔍', color: 'border-yellow-200 hover:border-yellow-400 hover:bg-yellow-50' },
    { to: '/beratung', title: 'Beratungsthemen', desc: 'Gesundheitsthemen, die Orientierung geben.', emoji: '💬', color: 'border-green-200 hover:border-green-400 hover:bg-green-50' },
    { to: '/wissen', title: 'Magazin & Ratgeber', desc: 'Aktuelle Artikel und Hintergrund-Stücke aus unserem Wissensbereich.', emoji: '📰', color: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50' },
    { to: '/', title: 'Startseite', desc: 'Zurück zum Einstieg von Behandlung.de.', emoji: '🏠', color: 'border-gray-200 hover:border-gray-400 hover:bg-gray-50' },
  ];

  return (
    <>
      <Helmet>
        <title>Seite nicht gefunden – Behandlung.de</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <section className="py-12 sm:py-16 bg-gradient-to-b from-blue-50/40 to-white min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase">Fehler 404</p>
            <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
              Diese Seite konnten wir nicht finden
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Vielleicht wurde die Adresse falsch eingegeben oder der Inhalt wurde verschoben. Über die folgenden Bereiche kommen Sie schnell ans Ziel:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cards.map((c) => (
              <button
                key={c.to}
                onClick={() => navigate(c.to)}
                className={`text-left bg-white rounded-lg border-2 p-5 transition shadow-sm hover:shadow-md ${c.color}`}
              >
                <div className="text-3xl mb-2">{c.emoji}</div>
                <div className="text-lg font-semibold text-gray-900 mb-1">{c.title}</div>
                <div className="text-sm text-gray-600 leading-snug">{c.desc}</div>
              </button>
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-gray-600 hover:text-blue-600 underline-offset-2 hover:underline"
            >
              ← Zurück zur vorherigen Seite
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is on admin pages
  const isAdminPage = location.pathname.startsWith('/admin') || location.pathname === '/login';

  // Routes that must never be indexed (auth-gated, transactional, internal).
  // robots.txt blocks crawling; this meta tag blocks indexing as defense-in-depth.
  const isNonIndexablePath =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/provider-') ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/contact' ||
    location.pathname === '/verify-email' ||
    location.pathname === '/email-change-verification' ||
    location.pathname === '/newsletter-verify' ||
    location.pathname === '/newsletter-success' ||
    location.pathname === '/newsletter-error';

  // Scroll to top when route changes
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Server-validated auth gate. localStorage flags below are kept as
  // cosmetic UI hints (Header, etc.) but never trusted for routing.
  const [authState, setAuthState] = React.useState<AuthState | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      const state = await resolveAuthState();
      if (cancelled) return;
      setAuthState(state);
      syncAuthLocalStorageHints(state);

      if (isAdminPath(location.pathname) && state.role !== 'admin') {
        navigate('/login', { replace: true });
        return;
      }
      if (isProviderPath(location.pathname) && state.role !== 'provider') {
        navigate('/provider-login', { replace: true });
        return;
      }
    };

    evaluate();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      evaluate();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [location.pathname, navigate]);

  const showAuthLoader = isProtectedPath(location.pathname) && authState === null;

  return (
    <SupabaseProvider>
      <SearchProvider>
        {isNonIndexablePath && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        <div className="min-h-screen bg-gray-50">
          {/* Header - show on all pages except admin pages */}
          {!isAdminPage && <Header />}
        
        {/* Main Routes — ErrorBoundary verhindert weisse Seite bei Render-
            Fehlern, zeigt stattdessen sichtbare Error-Box mit Stack-Trace. */}
        <ErrorBoundary fallbackContext={location.pathname}>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
        {showAuthLoader ? (
          <LoadingSpinner fullScreen />
        ) : (
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/symptome" element={<SymptomsPage />} />
          <Route path="/symptome/:slug" element={<SymptomDetail />} />
          <Route path="/krankheiten" element={<DiseasesPage />} />
          <Route path="/krankheiten/:slug" element={<DiseaseDetail />} />
          <Route path="/beratung" element={<ConsultationsPage />} />
          <Route path="/beratung/:slug" element={<ConsultationDetail />} />
          <Route path="/anbieter" element={<DoctorDirectory />} />
          <Route path="/anbieter/:doctorSlug" element={<DoctorDetail />} />
          <Route path="/register" element={<DoctorRegistration />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/wissen" element={<Blog />} />
          <Route path="/wissen/:slug" element={<BlogPost />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/provider-dashboard" element={<ProviderDashboard />} />
          <Route path="/provider-login" element={<ProviderLogin />} />
          <Route path="/provider-edit" element={<ProviderEdit />} />
          <Route path="/provider-account-settings" element={<ProviderAccountSettings />} />
          <Route path="/email-change-verification" element={<EmailChangeVerification />} />
          <Route path="/provider-fachbeitrag-neu" element={<ProviderFachbeitragCreate />} />
          <Route path="/premium-benefits" element={<PremiumBenefits />} />
          <Route path="/haftungsausschluss" element={<Haftungsausschluss />} />
          <Route path="/newsletter-success" element={<NewsletterSuccess />} />
          <Route path="/newsletter-error" element={<NewsletterError />} />
          <Route path="/newsletter-verify" element={<NewsletterVerification />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/contact" element={<ContactForm />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/login" element={<AdminLogin />} />
          
          {/* Debug Route — admin-only (auth-gate via /admin prefix) */}
          <Route path="/admin/debug" element={<SupabaseDebugger />} />

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        )}
        </Suspense>
        </ErrorBoundary>

        {/* Footer and Newsletter - show on all pages except admin pages and doctor detail */}
        {!isAdminPage && (
          <>
            {!location.pathname.startsWith('/anbieter/') && 
             location.pathname !== '/register' && 
             location.pathname !== '/provider-login' && 
             location.pathname !== '/provider-dashboard' && 
             location.pathname !== '/provider-edit' && 
             location.pathname !== '/provider-account-settings' && 
             location.pathname !== '/provider-fachbeitrag-neu' && 
             location.pathname !== '/premium-benefits' && 
             <NewsletterSignup />}
            <Footer />
          </>
        )}
        </div>
        <SupabaseConnectionMonitor />
        <CookieConsent />
        <CookieSettingsButton />
      </SearchProvider>
    </SupabaseProvider>
  );
}

export default App;